import { SizableText, Stack, XStack, YStack } from '@mythical/ui'
import { Button } from 'app/components/primitive/Button'
import {
  IconBookmark,
  IconBookmarkFilled,
  IconHeart,
} from 'app/components/primitive/Icon'
import { IconShare } from 'app/components/primitive/Icon/icons/IconShare'
import config from 'app/config'
import { useStores } from 'app/store'

import { IconHeartFull } from 'app/components/primitive/Icon/icons/IconHeartFull'
import { TextLink } from '../primitive/TextLink'
import ShareModal from 'app/components/content/ShareModal/ShareModal'
import { ContentLockButton } from './ContentLockButton'
import ContentRating from './ContentRating'

type ContentInfoProps = {
  category?: string
  categoryHref?: string
  datePublished?: string
  commentCount?: number
  likeCount?: number
  hasLiked?: boolean
  likeLoading?: boolean
  isVideo?: boolean
  title: string
  slug: string
  onLike?: () => void
  disableComments?: boolean
  series?: string
  isLocked?: boolean
  onRatingPress: (rating: number) => void
  currentRating?: number
  children?: React.ReactNode
  isInWatchLater?: boolean
  onToggleWatchLater?: () => void
  watchLaterLoading?: boolean
}

const ContentInfo = ({
  category,
  categoryHref,
  datePublished,
  commentCount,
  likeCount = 0,
  hasLiked = false,
  likeLoading = false,
  title,
  slug,
  onLike,
  disableComments,
  series,
  onRatingPress,
  currentRating,
  isVideo,
  isLocked,
  isInWatchLater = false,
  onToggleWatchLater,
  watchLaterLoading = false,
}: ContentInfoProps) => {
  const { userStore } = useStores()
  const loggedIn = !!userStore?.user?.id
  const seriesSlug = series ? `series/${series}/${slug}` : `content/${slug}`

  return (
    <YStack
      gap="$3.5"
      $gtXs={{ fd: 'row', ai: 'flex-start', jc: 'space-between' }}
    >
      <YStack fs={1} fg={1} gap="$2" maw={600}>
        {category && categoryHref && (
          <TextLink fow="500" size="$2" href={`/collection/${categoryHref}`}>
            {category}
          </TextLink>
        )}

        <SizableText ff="$heading" size="$l" tag="h1">
          {title}
        </SizableText>

        <XStack gap="$3" alignItems="center" flexWrap="wrap">
          <SizableText size="$1" color="$fgPrimary_op60">
            {datePublished}
            {!disableComments && (
              <SizableText size="$1" color="$fgPrimary_op60">
                {' '}
                • {commentCount} Comments
              </SizableText>
            )}
          </SizableText>
        </XStack>
      </YStack>

      <XStack
        gap="$2"
        fd="row"
        fw="wrap"
        ai="flex-start"
        $gtXs={{
          jc: 'flex-end',
          fs: isLocked ? 0 : 1,
          miw: 280,
          maw: 300,
        }}
        $gtLg={{ maw: 330 }}
      >
        {isVideo && !isLocked && (
          <Stack $gtXs={{ ai: 'flex-end' }}>
            <ContentRating
              onRatingPress={onRatingPress}
              currentRating={currentRating}
            />
          </Stack>
        )}

        {isLocked && (
          <ContentLockButton fg={1} $gtXs={{ fg: 0 }} $gtLg={{ size: '$5' }} />
        )}

        <ShareModal
          href={`${config.siteUrl}/${seriesSlug}`}
          trigger={
            <Button
              $gtLg={{ size: '$5' }}
              circular
              theme="secondary"
              icon={<IconShare size={20} />}
            />
          }
        />

        {loggedIn && !isLocked && (
          <Button
            $gtLg={{ size: '$5' }}
            scaleSpace={0.4}
            loading={likeLoading}
            onPress={onLike}
            icon={
              hasLiked ? <IconHeartFull size={20} /> : <IconHeart size={20} />
            }
          >
            {`${likeCount}`}
          </Button>
        )}

        {loggedIn && !isLocked && isVideo && (
          <Button
            theme="secondary"
            scaleSpace={0.4}
            miw={120}
            loading={watchLaterLoading}
            onPress={onToggleWatchLater}
            icon={
              isInWatchLater ? (
                <IconBookmarkFilled size={20} />
              ) : (
                <IconBookmark size={20} />
              )
            }
            $gtLg={{ size: '$5', miw: 140 }}
          >
            {isInWatchLater ? 'Saved to Watch Later' : 'Save for Later'}
          </Button>
        )}
      </XStack>
    </YStack>
  )
}

export default ContentInfo
